import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import Footer from "../pages/Footer";
import Weblinks from "../pages/Weblinks";
import TranslatedText from "./TranslatedText";

function Layout({ children }) {
  const [userRole, setUserRole] = useState(null);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      const parsedUser = JSON.parse(user);
      setUserRole(parsedUser.role);
    } else {
      setUserRole(null);
    }
  }, [location.pathname]);

  const isAdminRoute = location.pathname === "/admin";
  const isDashboardRoute = location.pathname === "/dashboard";
  const isLoginRoute = location.pathname === "/login";
  const showNavbar = !isAdminRoute && !isDashboardRoute && !isLoginRoute;

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
      {/* 🌟 Header */}
      {showNavbar && (
        <div>
          {/* Top utility bar */}
          <div className="w-full px-4 py-1 text-xs text-gray-600 bg-gray-100 border-b border-gray-200">
            <div className="flex items-center justify-end mx-auto space-x-4 max-w-7xl">
              <a
                href="/sitemap"
                className="transition-colors hover:text-gray-800 hover:underline"
              >
                {i18n.language === "mr" ? "साइट मैप" : "Site Map"}
              </a>
              <span className="text-gray-400">|</span>
              <a
                href="/accessibility"
                className="transition-colors hover:text-gray-800 hover:underline"
              >
                {i18n.language === "mr" ? "सुलभ वापर सुविधा" : "Accessibility"}
              </a>
            </div>
          </div>

          {/* Language Switcher for mobile only, below utility bar */}
          <div className="block w-full px-4 py-2 bg-white border-b border-gray-200 sm:hidden">
            <div className="flex justify-end">
              <LanguageSwitcher />
            </div>
          </div>

          {/* Tricolor strip */}
          <div
            className="w-full h-2"
            style={{
              background:
                "linear-gradient(to right, #FF6600 0%, #FFD700 50%, #FF6600 100%)",
            }}
          ></div>

          {/* Main Header */}
          <div className="px-4 py-3 sm:py-4 sm:px-6">
            <div className="items-center hidden w-full gap-4 sm:grid sm:grid-cols-3">
              {/* Left Logos + District Name */}
              <div className="flex items-center justify-start space-x-3 sm:space-x-4">
                <div className="flex items-center space-x-2">
                  <img
                    src="/assets/logos/header1logo.png"
                    alt="Government of India"
                    className="w-auto h-10 sm:h-16"
                  />
                  <img
                    src="/assets/logos/header2logo.png"
                    alt="Government of Maharashtra"
                    className="w-auto h-8 sm:h-12"
                  />
                </div>
                <div className="hidden w-px h-12 bg-gray-300 sm:block"></div>
                <div className="flex flex-col text-left sm:text-left">
                  <div className="text-[14px] sm:text-[18px] text-gray-800 tracking-wide">
                    जिल्हा गडचिरोली
                  </div>
                  <div className="text-[16px] sm:text-[22px] font-bold text-gray-800 tracking-wide">
                    DISTRICT GADCHIROLI
                  </div>
                </div>
              </div>

              {/* Center Title */}
              <div className="flex items-center justify-center">
                <div className="flex flex-col items-center justify-center text-center">
                  {/* Main Title */}
                  <div
                    className="font-bold text-black header-title"
                    style={{
                      fontSize: i18n.language === "mr" ? "50px" : "28px", // manually controlled sizes
                    }}
                  >
                    {t("header.pulic.request")}
                  </div>


                  {/* Subtitle */}
                  <div className="mt-1 font-normal text-gray-800 sm:mt-2 header-subtitle"
                  style={{
                      fontSize: i18n.language === "mr" ? "25px" : "15px", // manually controlled sizes
                    }}>
                    {t("header.public.note")}
                  </div>
                </div>
              </div>


              {/* Right - Language Switcher (desktop) + Ministers */}
              <div className="flex flex-col items-end justify-center">
                {/* Desktop Language Switcher */}
                <div className="mb-2">
                  <LanguageSwitcher />
                </div>
                {/* Always side by side */}
                <div className="flex flex-row space-x-4">
                  {/* CM */}
                  <div className="flex flex-col items-center text-center">
                    <img
                      src="/assets/logos/CM.png"
                      alt="Chief Minister"
                      className="object-cover w-auto h-12 sm:h-14"
                    />
                    <div className="mt-1 text-[10px] sm:text-xs leading-snug">
                      <p className="font-bold">
                        {t("header.Shri. Devendra Fadnavis")}
                      </p>
                      <p>{t("header.cm.note")}</p>
                      <p>{t("header.cm.note1")}</p>
                    </div>
                  </div>
                  {/* Ashish Jaiswal */}
                  <div className="flex flex-col items-center text-center">
                    <img
                      src="/assets/logos/Shri. Ashish Jaiswal Ministe.png"
                      alt="Shri Ashish Jaiswal"
                      className="object-cover w-auto h-12 sm:h-14"
                    />
                    <div className="mt-1 text-[10px] sm:text-xs leading-snug">
                      <p className="font-bold">
                        {t("header.Shri. Ashish Jaiswal")}
                      </p>
                      <p>{t("header.Co-Guardian Minister, Gadchiroli")}</p>
                      <p>{t("header.note2")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Header */}
            <div className="flex flex-col items-center w-full gap-4 sm:hidden">
              {/* Left Logos + District Name */}
              <div className="flex items-center justify-start w-full space-x-3">
                <div className="flex items-center space-x-2">
                  <img
                    src="/assets/logos/header1logo.png"
                    alt="Government of India"
                    className="w-auto h-10"
                  />
                  <img
                    src="/assets/logos/header2logo.png"
                    alt="Government of Maharashtra"
                    className="w-auto h-8"
                  />
                </div>
                <div className="flex flex-col text-left">
                  <div className="text-[14px] text-gray-800 tracking-wide">
                    जिल्हा गडचिरोली
                  </div>
                  <div className="text-[16px] font-bold text-gray-800 tracking-wide">
                    DISTRICT GADCHIROLI
                  </div>
                </div>
              </div>

              {/* Center Title */}
              <div className="flex items-center justify-center w-full">
                <div className="flex flex-col items-center justify-center text-center">
                  {/* Main Title */}
                  <div className="text-2xl font-bold text-black header-title">
                    {t("header.pulic.request")}
                  </div>
                  {/* Subtitle */}
                  <div className="text-[11px] font-normal text-gray-800 mt-1 header-subtitle">
                    {t("header.public.note")}
                  </div>
                </div>
              </div>

              {/* Ministers */}
              <div className="flex flex-row justify-center w-full space-x-4">
                {/* CM */}
                <div className="flex flex-col items-center text-center">
                  <img
                    src="/assets/logos/CM.png"
                    alt="Chief Minister"
                    className="object-cover w-auto h-12"
                  />
                  <div className="mt-1 text-[10px] leading-snug">
                    <p className="font-bold">
                      {t("header.Shri. Devendra Fadnavis")}
                    </p>
                    <p>{t("header.cm.note")}</p>
                    <p>{t("header.cm.note1")}</p>
                  </div>
                </div>
                {/* Ashish Jaiswal */}
                <div className="flex flex-col items-center text-center">
                  <img
                    src="/assets/logos/Shri. Ashish Jaiswal Ministe.png"
                    alt="Shri Ashish Jaiswal"
                    className="object-cover w-auto h-12"
                  />
                  <div className="mt-1 text-[10px] leading-snug">
                    <p className="font-bold">
                      {t("header.Shri. Ashish Jaiswal")}
                    </p>
                    <p>{t("header.Co-Guardian Minister, Gadchiroli")}</p>
                    <p>{t("header.note2")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Bar */}
          <nav
            className={`text-white shadow-lg border-t ${i18n.language === "mr" ? "lang-mr" : ""
              }`}
            style={{
              background:
                "linear-gradient(135deg, #B8860B 0%, #DAA520 50%, #B8860B 100%)",
              borderTopColor: "#B8860B",
            }}
          >
            <div className="px-4 mx-auto max-w-7xl sm:px-6">
              <div className="flex items-center justify-center h-12 space-x-4 text-xs font-medium tracking-wide uppercase sm:h-14 sm:space-x-8 sm:text-sm">
                <Link
                  to="/"
                  className={`px-2 sm:px-4 py-2 border-b-2 transition-all duration-300 ${location.pathname === "/"
                    ? "border-yellow-300 text-yellow-100"
                    : "border-transparent hover:border-yellow-200 hover:bg-yellow-900/20"
                    }`}
                >
                  {t("nav.home")}
                </Link>
                <Link
                  to="/complaint/new"
                  className={`px-2 sm:px-4 py-2 border-b-2 transition-all duration-300 ${location.pathname === "/complaint/new"
                    ? "border-yellow-300 text-yellow-100"
                    : "border-transparent hover:border-yellow-200 hover:bg-yellow-900/20"
                    }`}
                >
                  <span className="hidden md:inline">
                    {t("nav.newComplaint") || "Register Complaint"}
                  </span>
                  <span className="md:hidden">
                    {t("nav.newComplaint.short") || "Register"}
                  </span>
                </Link>
                <Link
                  to="/complaint/status"
                  className={`px-2 sm:px-4 py-2 border-b-2 transition-all duration-300 ${location.pathname === "/complaint/status"
                    ? "border-yellow-300 text-yellow-100"
                    : "border-transparent hover:border-yellow-200 hover:bg-yellow-900/20"
                    }`}
                >
                  <span className="hidden md:inline">
                    {t("nav.trackComplaint")}
                  </span>
                  <span className="md:hidden">
                    {t("nav.trackComplaint.short") || "Track"}
                  </span>
                </Link>
                <Link
                  to="/complaint/login"
                  className={`px-2 sm:px-4 py-2 border-b-2 transition-all duration-300 ${location.pathname === "/complaint/login"
                    ? "border-yellow-300 text-yellow-100"
                    : "border-transparent hover:border-yellow-200 hover:bg-yellow-900/20"
                    } relative group`}
                >
                  <span className="hidden lg:inline">
                    {t("nav.login") || "Officer Login"}
                  </span>
                  <span className="lg:hidden">
                    {t("nav.login") || "Officer"}
                  </span>
                  <div className="absolute z-50 px-3 py-2 mb-2 text-xs text-white transition-opacity duration-200 transform -translate-x-1/2 bg-gray-900 rounded-lg opacity-0 bottom-full left-1/2 group-hover:opacity-100 whitespace-nowrap">
                  <TranslatedText>  For Government Staff Only </TranslatedText>
                    <div className="absolute transform -translate-x-1/2 border-4 border-transparent top-full left-1/2 border-t-gray-900"></div>
                  </div>
                </Link>
              </div>
            </div>
          </nav>
        </div>
      )}

      {/* Dashboard/Admin Content */}
      {(isAdminRoute || isDashboardRoute) && (
        <main
          className="min-h-screen"
          style={{
            background:
              "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 30%, #e2e8f0 70%, #f8fafc 100%)",
          }}
        >
          <div className="h-px shadow-sm bg-gradient-to-r from-transparent via-amber-300 to-transparent"></div>
          <div className="relative">{children}</div>
        </main>
      )}

      {/* Main Content */}
      {showNavbar && (
        <main className="w-full max-w-[1400px] mx-auto py-8 px-4 sm:px-8 lg:px-16 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl mt-6">
          {children}
        </main>
      )}

      {location.pathname === "/" && <Weblinks />}

      {/* Footer */}
      <div className="mt-5">
        <Footer />
      </div>
    </div>
  );
}

export default Layout;
